import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  TRIGGERS,
  RESTRICTIONS,
  EFFECT_FORMATS,
  PRE_CONDITION_CHECKS,
  ZONES,
  SCOPES,
  PASSIVE_SCOPES,
  TARGET_SCOPES,
  FILTER_COMPARISONS,
  FILTER_FIELDS,
  FILTER_CARD_TYPES,
  FILTER_CARD_LOCATIONS,
  PASSIVE_MODIFIER_STATS,
  PASSIVE_EFFECT_TYPES,
  createPassiveEffectDetails,
  CANNOT_BE_KOD_TYPES,
  createCannotBeKodDetails,
  createExtraNameDetails,
  PASSIVE_MULTIPLIER_TYPES,
  createPassiveMultiplier,
  parseZonesFromEditor,
  isBooleanFilterField,
  STEP_TYPES,
  ACTION_TYPES,
  ACTION_DETAIL_TEMPLATES,
  IF_STEP_CHECK_TYPES,
  getIfStepCheckDef,
  getIfCheckDefaults,
  createIfCheck,
  createPreCondition,
  getPreConditionCheckDefaults,
  getSearchDeckPlaceRestOptions,
  SEARCH_DECK_DEFAULT_TITLES,
  SEARCH_DECK_FORCE_NO_SELECTION_TITLES,
  createDefaultSearchDeckDetails,
  createDefaultReturnDonsDetails,
  createFilter,
  getFilterFieldDefaults,
  createTargetStep,
  createActionStep,
  createIfStep,
  createSearchDeckStep,
  createReturnDonsStep,
  createPromptStep,
  createRevealStep,
  createDefaultRevealDetails,
  REVEAL_WHO_OPTIONS,
  REVEAL_CARD_REF_STEP_TYPES,
  ACTION_DETAIL_REF_LABELS,
  getActionDetailRefSpecialValues,
  getActionDetailRefStepTypes,
  getRefSelectableSteps,
  getActionDetailRefKeys,
  parseActionDetailsSafe,
  stripStructuredActionDetails,
  actionDetailHasDuration,
  getActionDurationOptions,
  getActionStructuredDetailKeys,
  DECK_POSITIONS,
  parseDetailsJson,
  createEffect,
  createEffectsFromCard,
  createCardEntry,
  buildMultiCardEffectsJson,
  buildEffect,
  buildFilter,
  importFiltersForEditor,
  importEffect,
  extractTraitsFromEffectText,
  extractCardNamesFromEffectText,
  ADD_EFFECT_KEYWORD_TRIGGERS,
  isKeywordNestedEffect,
  parseAddEffectNestedEffect,
  buildAddEffectNestedEffect,
} from './effectSchema';
import EffectsEditorCardList from './EffectsEditorCardList';
import {
  deleteCardEffect,
  getCardEffect,
  saveCardEffect,
} from '../../services/api/apiService';
import { EFFECT_SOURCE } from './EffectsEditorCardList';
import CardPreview from './CardPreview';

import './style.css';

const updateListItem = (list, index, patch) =>
  list.map((item, i) => (i === index ? { ...item, ...patch } : item));

const removeListItem = (list, index) => list.filter((_, i) => i !== index);

const moveListItem = (list, index, direction) => {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= list.length) return list;
  const next = [...list];
  [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
  return next;
};

function Field({ label, children }) {
  return (
    <div className="effects-editor__field">
      {label && <label>{label}</label>}
      {children}
    </div>
  );
}

function StepRefSelect({ value, onChange, steps, stepTypes = ['target', 'searchDeck'], specialValues = [], placeholder }) {
  const options = getRefSelectableSteps(steps, stepTypes);
  const optionIds = new Set([
    ...options.map((o) => o.stepId),
    ...specialValues.map((o) => o.value),
  ]);
  const showCustom = value && !optionIds.has(value);

  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      title={placeholder}
    >
      <option value="">{placeholder || 'Select step…'}</option>
      {specialValues.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
      {options.map((opt) => (
        <option key={opt.stepId} value={opt.stepId}>{opt.label}</option>
      ))}
      {showCustom && (
        <option value={value}>{value} (not in effect steps)</option>
      )}
    </select>
  );
}

function ActionDetailsEditor({
  actionType,
  detailsJson,
  onChange,
  steps,
  traitSuggestions = [],
  nameSuggestions = [],
}) {
  let parseError = null;
  let details = {};

  try {
    details = parseDetailsJson(detailsJson || '{}') ?? {};
  } catch (e) {
    parseError = e.message;
    details = parseActionDetailsSafe(detailsJson) ?? {};
  }

  const refKeys = getActionDetailRefKeys(details, actionType);
  const otherJson = JSON.stringify(stripStructuredActionDetails(details, actionType), null, 2);
  const nestedEffectEditor = actionType === 'AddEffect'
    ? parseAddEffectNestedEffect(details.effect)
    : null;
  const nestedEffectJson = actionType === 'AddEffect'
    ? JSON.stringify(details.effect || {}, null, 2)
    : '';

  const commitDetails = (next) => {
    const cleaned = Object.fromEntries(
      Object.entries(next).filter(([, v]) => v !== undefined && v !== ''),
    );
    onChange(JSON.stringify(cleaned, null, 2));
  };

  const updateRef = (key, refValue) => {
    const next = { ...details };
    if (refValue) next[key] = refValue;
    else delete next[key];
    commitDetails(next);
  };

  const updateDetail = (key, value) => {
    commitDetails({ ...details, [key]: value });
  };

  const preserveStructuredFields = (parsed) => {
    const merged = { ...parsed };
    for (const key of refKeys) {
      if (details[key] !== undefined) merged[key] = details[key];
    }
    for (const key of getActionStructuredDetailKeys(actionType, details)) {
      if (details[key] !== undefined) merged[key] = details[key];
    }
    return merged;
  };

  const updateOtherJson = (json) => {
    try {
      const parsed = JSON.parse(json || '{}');
      commitDetails(preserveStructuredFields(parsed));
    } catch {
      // keep editing until valid JSON
    }
  };

  const updateNestedEffectJson = (json) => {
    try {
      const parsed = JSON.parse(json || '{}');
      if (!parsed.trigger) return;
      commitDetails({ ...details, effect: parsed });
    } catch {
      // keep editing until valid JSON
    }
  };

  const updateNestedEffectTrigger = (trigger) => {
    const updated = {
      ...nestedEffectEditor,
      trigger,
      format: 'keyword',
    };
    if (trigger === 'CannotBeKod' && !updated.cannotBeKodDetails) {
      updated.cannotBeKodDetails = createCannotBeKodDetails();
    }
    if (trigger === 'ExtraName' && !updated.extraNameDetails) {
      updated.extraNameDetails = createExtraNameDetails();
    }
    commitDetails({
      ...details,
      effect: buildAddEffectNestedEffect(updated),
    });
  };

  if (parseError) {
    return (
      <>
        <p className="effects-editor__hint effects-editor__status--error">
          Invalid details JSON: {parseError}
        </p>
        <Field label="Details (JSON)">
          <textarea value={detailsJson} onChange={(e) => onChange(e.target.value)} />
        </Field>
      </>
    );
  }

  return (
    <>
      {refKeys.length > 0 && (
        <div className="effects-editor__row">
          {refKeys.map((key) => (
            <Field key={key} label={ACTION_DETAIL_REF_LABELS[key] || key}>
              <StepRefSelect
                value={typeof details[key] === 'string' ? details[key] : ''}
                onChange={(refValue) => updateRef(key, refValue)}
                steps={steps}
                stepTypes={getActionDetailRefStepTypes(key)}
                specialValues={getActionDetailRefSpecialValues(actionType, key)}
                placeholder={`Select ${key}…`}
              />
            </Field>
          ))}
        </div>
      )}
      {(actionDetailHasDuration(actionType, details)
        || actionType === 'ReturnToOwnerDeck'
        || actionType === 'ReturnLifeToOwnerDeck'
        || actionType === 'AddDon') && (
        <div className="effects-editor__row">
          {actionDetailHasDuration(actionType, details) && (
            <Field label={actionType === 'AddEffect' ? 'Effect duration' : 'Duration'}>
              <select
                value={details.duration || 'EndOfTurn'}
                onChange={(e) => updateDetail('duration', e.target.value)}
              >
                {getActionDurationOptions(actionType).map((duration) => (
                  <option key={duration} value={duration}>{duration}</option>
                ))}
                {details.duration && !getActionDurationOptions(actionType).includes(details.duration) && (
                  <option value={details.duration}>{details.duration}</option>
                )}
              </select>
            </Field>
          )}
          {actionType === 'ReturnToOwnerDeck' && (
            <Field label="Deck position">
              <select
                value={details.position || 'Bottom'}
                onChange={(e) => updateDetail('position', e.target.value)}
              >
                {DECK_POSITIONS.map((pos) => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
                {details.position && !DECK_POSITIONS.includes(details.position) && (
                  <option value={details.position}>{details.position}</option>
                )}
              </select>
            </Field>
          )}
          {actionType === 'ReturnLifeToOwnerDeck' && (
            <>
              <Field label="Scope">
                <select
                  value={details.scope || 'Player'}
                  onChange={(e) => updateDetail('scope', e.target.value)}
                >
                  {SCOPES.map((scope) => (
                    <option key={scope} value={scope}>{scope}</option>
                  ))}
                  {details.scope && !SCOPES.includes(details.scope) && (
                    <option value={details.scope}>{details.scope}</option>
                  )}
                </select>
              </Field>
              <Field label="Amount">
                <input
                  type="number"
                  min="1"
                  value={details.amount ?? 1}
                  onChange={(e) => updateDetail('amount', Number(e.target.value) || 1)}
                />
              </Field>
              <Field label="Deck position">
                <select
                  value={details.position || 'Bottom'}
                  onChange={(e) => updateDetail('position', e.target.value)}
                >
                  {DECK_POSITIONS.map((pos) => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                  {details.position && !DECK_POSITIONS.includes(details.position) && (
                    <option value={details.position}>{details.position}</option>
                  )}
                </select>
              </Field>
            </>
          )}
          {actionType === 'AddDon' && (
            <>
              <Field label="Count">
                <input
                  type="number"
                  min="1"
                  value={details.count ?? 1}
                  onChange={(e) => updateDetail('count', Number(e.target.value) || 1)}
                />
              </Field>
              <Field label="Is rested">
                <select
                  value={String(details.isRested === true)}
                  onChange={(e) => updateDetail('isRested', e.target.value === 'true')}
                >
                  <option value="false">No (active)</option>
                  <option value="true">Yes (rested)</option>
                </select>
              </Field>
            </>
          )}
        </div>
      )}
      {actionType === 'AddEffect' && actionDetailHasDuration(actionType, details) && (
        <p className="effects-editor__hint">
          How long the granted ability stays on the target card.
        </p>
      )}
      {actionType === 'AddBlockerRestriction' && (
        <>
          <h3>Blocker restriction filters</h3>
          <p className="effects-editor__hint">
            Optional. Restrict which opponent blockers cannot be activated (e.g. power gte 5000).
            Leave empty to block all blockers.
          </p>
          <FilterEditor
            filters={importFiltersForEditor(details.filters)}
            onChange={(editorFilters) => {
              const built = editorFilters.map(buildFilter);
              commitDetails({
                ...details,
                filters: built.length > 0 ? built : [],
              });
            }}
            steps={steps}
            traitSuggestions={traitSuggestions}
            nameSuggestions={nameSuggestions}
          />
        </>
      )}
      {actionType === 'AddEffect' && (
        <section>
          <h3>Temporary effect</h3>
          <p className="effects-editor__hint">
            Keyword or trait granted to the selected target until effect duration expires.
          </p>
          {isKeywordNestedEffect(details.effect) ? (
            <>
              <div className="effects-editor__row">
                <Field label="Trigger">
                  <select
                    value={nestedEffectEditor?.trigger || 'Rush'}
                    onChange={(e) => updateNestedEffectTrigger(e.target.value)}
                  >
                    {ADD_EFFECT_KEYWORD_TRIGGERS.map((trigger) => (
                      <option key={trigger} value={trigger}>{trigger}</option>
                    ))}
                    {nestedEffectEditor?.trigger
                      && !ADD_EFFECT_KEYWORD_TRIGGERS.includes(nestedEffectEditor.trigger) && (
                      <option value={nestedEffectEditor.trigger}>{nestedEffectEditor.trigger}</option>
                    )}
                  </select>
                </Field>
              </div>
              {nestedEffectEditor?.trigger === 'CannotBeKod' && (
                <CannotBeKodDetailsEditor
                  cannotBeKodDetails={nestedEffectEditor.cannotBeKodDetails}
                  onChange={(cannotBeKodDetails) =>
                    commitDetails({
                      ...details,
                      effect: buildAddEffectNestedEffect({
                        ...nestedEffectEditor,
                        cannotBeKodDetails,
                      }),
                    })
                  }
                  steps={steps}
                  traitSuggestions={traitSuggestions}
                  nameSuggestions={nameSuggestions}
                />
              )}
              {nestedEffectEditor?.trigger === 'ExtraName' && (
                <ExtraNameDetailsEditor
                  extraNameDetails={nestedEffectEditor.extraNameDetails}
                  onChange={(extraNameDetails) =>
                    commitDetails({
                      ...details,
                      effect: buildAddEffectNestedEffect({
                        ...nestedEffectEditor,
                        extraNameDetails,
                      }),
                    })
                  }
                  nameSuggestions={nameSuggestions}
                />
              )}
            </>
          ) : (
            <>
              <p className="effects-editor__hint">
                This temporary effect uses effect steps; step editor coming later.
              </p>
              <Field label="Temporary effect (JSON)">
                <textarea
                  value={nestedEffectJson}
                  onChange={(e) => updateNestedEffectJson(e.target.value)}
                  rows={Math.min(16, Math.max(6, nestedEffectJson.split('\n').length + 1))}
                />
              </Field>
            </>
          )}
        </section>
      )}
      {refKeys.length === 0 && actionType !== 'AddBlockerRestriction' && actionType !== 'AddEffect' && (
        <p className="effects-editor__hint">
          No step references in this action. Add a target or search deck step, or pick an action that uses refs.
        </p>
      )}
      {otherJson !== '{}' && (
        <Field label="Other details (JSON)">
          <textarea
            value={otherJson}
            onChange={(e) => updateOtherJson(e.target.value)}
            rows={Math.min(12, Math.max(3, otherJson.split('\n').length + 1))}
          />
        </Field>
      )}
    </>
  );
}

function PreConditionEditor({
  preConditions,
  onChange,
  steps = [],
  traitSuggestions = [],
  nameSuggestions = [],
}) {
  return (
    <section>
      <h3>Pre-conditions</h3>
      <div className="effects-editor__actions">
        <button
          type="button"
          className="effects-editor__btn"
          onClick={() => onChange([...preConditions, createPreCondition()])}
        >
          Add pre-condition
        </button>
      </div>
      {preConditions.map((pc, index) => {
        const def = PRE_CONDITION_CHECKS.find((c) => c.id === pc.check);
        return (
          <div className="effects-editor__card" key={`pc-${index}`}>
            <div className="effects-editor__row">
              <Field label="Check">
                <select
                  value={pc.check}
                  onChange={(e) => {
                    const check = e.target.value;
                    onChange(updateListItem(preConditions, index, {
                      check,
                      ...getPreConditionCheckDefaults(check, { nameSuggestions }),
                    }));
                  }}
                >
                  {PRE_CONDITION_CHECKS.map((check) => (
                    <option key={check.id} value={check.id}>{check.label}</option>
                  ))}
                </select>
              </Field>
              {def?.fields.includes('zone') && (
                <Field label="Zone">
                  <select
                    value={pc.zone}
                    onChange={(e) =>
                      onChange(updateListItem(preConditions, index, { zone: e.target.value }))
                    }
                  >
                    <option value="CostArea">CostArea</option>
                  </select>
                </Field>
              )}
              {def?.fields.includes('scope') && (
                <Field label="Scope">
                  <select
                    value={pc.scope || 'Player'}
                    onChange={(e) =>
                      onChange(updateListItem(preConditions, index, { scope: e.target.value }))
                    }
                  >
                    {SCOPES.map((scope) => (
                      <option key={scope} value={scope}>{scope}</option>
                    ))}
                  </select>
                </Field>
              )}
              {def?.fields.includes('count') && (
                <Field label={def.countLabel || 'Count'}>
                  <input
                    type="number"
                    min="0"
                    value={pc.count}
                    onChange={(e) =>
                      onChange(updateListItem(preConditions, index, { count: e.target.value }))
                    }
                  />
                </Field>
              )}
              {def?.fields.includes('name') && (
                <Field label="Leader name">
                  <input
                    value={pc.name || ''}
                    onChange={(e) =>
                      onChange(updateListItem(preConditions, index, { name: e.target.value }))
                    }
                    placeholder="e.g. Kozuki Oden"
                    list={pc.check === 'leaderName' && nameSuggestions.length > 0
                      ? `leader-name-suggestions-${index}`
                      : undefined}
                  />
                  {pc.check === 'leaderName' && nameSuggestions.length > 0 && (
                    <datalist id={`leader-name-suggestions-${index}`}>
                      {nameSuggestions.map((name) => (
                        <option key={name} value={name} />
                      ))}
                    </datalist>
                  )}
                </Field>
              )}
            </div>
            {def?.fields.includes('filters') && (
              <>
                <h3>{def.filtersLabel || 'Filters'}</h3>
                <p className="effects-editor__hint">
                  {def.filtersHint || 'Optional.'}
                </p>
                <FilterEditor
                  filters={pc.filters || []}
                  onChange={(filters) =>
                    onChange(updateListItem(preConditions, index, { filters }))
                  }
                  steps={steps}
                  traitSuggestions={traitSuggestions}
                  nameSuggestions={nameSuggestions}
                />
              </>
            )}
            <button
              type="button"
              className="effects-editor__btn effects-editor__btn--danger"
              onClick={() => onChange(removeListItem(preConditions, index))}
            >
              Remove
            </button>
          </div>
        );
      })}
    </section>
  );
}

function FilterEditor({
  filters,
  onChange,
  steps = [],
  traitSuggestions = [],
  nameSuggestions = [],
}) {
  const filterSuggestions = { traitSuggestions, nameSuggestions };
  const targetSteps = steps.filter((s) => s.type === 'target');
  return (
    <div>
      <div className="effects-editor__actions">
        <button
          type="button"
          className="effects-editor__btn"
          onClick={() => onChange([...filters, createFilter()])}
        >
          Add filter
        </button>
      </div>
      {filters.map((filter, index) => (
        <div className="effects-editor__card" key={`filter-${index}`}>
          <div className="effects-editor__row">
            <Field label="Field">
              <select
                value={filter.field}
                onChange={(e) => {
                  const field = e.target.value;
                  onChange(updateListItem(filters, index, {
                    field,
                    ...getFilterFieldDefaults(field, filterSuggestions),
                  }));
                }}
              >
                {FILTER_FIELDS.map((field) => (
                  <option key={field} value={field}>{field}</option>
                ))}
              </select>
            </Field>
            {(filter.field !== 'isBlocker' && filter.field !== 'isSelf') && (
              <Field label="Comparison">
                <select
                  value={filter.comparison || ''}
                  onChange={(e) => {
                    const comparison = e.target.value;
                    const patch = { comparison };
                    if (comparison === 'neqRef' || comparison === 'notContainsRef') {
                      patch.ref = filter.ref || '';
                      patch.value = '';
                    } else if (filter.comparison === 'neqRef' || filter.comparison === 'notContainsRef') {
                      patch.ref = '';
                    }
                    onChange(updateListItem(filters, index, patch));
                  }}
                >
                  <option value="">(none)</option>
                  {FILTER_COMPARISONS.map((comparison) => (
                    <option key={comparison} value={comparison}>{comparison}</option>
                  ))}
                </select>
              </Field>
            )}
            {(filter.comparison === 'neqRef' || filter.comparison === 'notContainsRef') ? (
              <Field label="Target step">
                <StepRefSelect
                  value={filter.ref || ''}
                  onChange={(ref) =>
                    onChange(updateListItem(filters, index, { ref }))
                  }
                  steps={targetSteps}
                  placeholder="Select target step…"
                />
              </Field>
            ) : isBooleanFilterField(filter.field) ? (
              <Field label="Value">
                <select
                  value={filter.value === false || filter.value === 'false' ? 'false' : 'true'}
                  onChange={(e) =>
                    onChange(updateListItem(filters, index, { value: e.target.value === 'true' }))
                  }
                >
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              </Field>
            ) : filter.field === 'type' && filter.comparison === 'oneOf' ? (
              <Field label="Types">
                <div className="effects-editor__checkbox-row">
                  {FILTER_CARD_TYPES.map((cardType) => {
                    const selectedTypes = (typeof filter.value === 'string'
                      ? filter.value.split(',').map((s) => s.trim())
                      : Array.isArray(filter.value)
                        ? filter.value
                        : []
                    ).filter(Boolean);
                    const checked = selectedTypes.includes(cardType);
                    return (
                      <label key={cardType} className="effects-editor__checkbox-label">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...selectedTypes, cardType]
                              : selectedTypes.filter((t) => t !== cardType);
                            onChange(updateListItem(filters, index, {
                              value: next.join(', '),
                            }));
                          }}
                        />
                        {cardType}
                      </label>
                    );
                  })}
                </div>
              </Field>
            ) : filter.field === 'type' ? (
              <Field label="Value">
                <select
                  value={filter.value || 'Character'}
                  onChange={(e) =>
                    onChange(updateListItem(filters, index, { value: e.target.value }))
                  }
                >
                  {FILTER_CARD_TYPES.map((cardType) => (
                    <option key={cardType} value={cardType}>{cardType}</option>
                  ))}
                  {filter.value && !FILTER_CARD_TYPES.includes(filter.value) && (
                    <option value={filter.value}>{filter.value}</option>
                  )}
                </select>
              </Field>
            ) : filter.field === 'location' ? (
              <Field label="Value">
                <select
                  value={filter.value || 'Hand'}
                  onChange={(e) =>
                    onChange(updateListItem(filters, index, { value: e.target.value }))
                  }
                >
                  {FILTER_CARD_LOCATIONS.map((loc) => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                  {filter.value && !FILTER_CARD_LOCATIONS.includes(filter.value) && (
                    <option value={filter.value}>{filter.value}</option>
                  )}
                </select>
              </Field>
            ) : (
              <Field label={filter.comparison === 'oneOf' ? 'Values (comma-separated)' : 'Value'}>
                <input
                  value={filter.value ?? ''}
                  onChange={(e) =>
                    onChange(updateListItem(filters, index, { value: e.target.value }))
                  }
                  list={
                    filter.field === 'cardTraits' && traitSuggestions.length > 0
                      ? `trait-suggestions-${index}`
                      : filter.field === 'name' && nameSuggestions.length > 0
                        ? `name-filter-suggestions-${index}`
                        : undefined
                  }
                />
                {filter.field === 'cardTraits' && traitSuggestions.length > 0 && (
                  <datalist id={`trait-suggestions-${index}`}>
                    {traitSuggestions.map((trait) => (
                      <option key={trait} value={trait} />
                    ))}
                  </datalist>
                )}
                {filter.field === 'name' && nameSuggestions.length > 0 && (
                  <datalist id={`name-filter-suggestions-${index}`}>
                    {nameSuggestions.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                )}
              </Field>
            )}
          </div>
          <button
            type="button"
            className="effects-editor__btn effects-editor__btn--danger"
            onClick={() => onChange(removeListItem(filters, index))}
          >
            Remove filter
          </button>
        </div>
      ))}
    </div>
  );
}

function SearchDeckDetailsEditor({
  details,
  onChange,
  steps = [],
  traitSuggestions = [],
  nameSuggestions = [],
}) {
  const d = details || createDefaultSearchDeckDetails();
  const update = (patch) => onChange({ ...d, ...patch });

  return (
    <>
      <div className="effects-editor__row">
        <Field label="Search mode">
          <select
            value={d.fullSearch ? 'full' : 'peek'}
            onChange={(e) => update({ fullSearch: e.target.value === 'full' })}
          >
            <option value="peek">Peek from top (amount)</option>
            <option value="full">Search entire deck</option>
          </select>
        </Field>
        {!d.fullSearch && (
          <Field label="Amount">
            <input
              type="number"
              min="1"
              value={d.amount ?? 1}
              onChange={(e) => update({ amount: e.target.value })}
            />
          </Field>
        )}
        <Field label="Force no selection">
          <select
            value={String(d.forceNoSelection)}
            onChange={(e) => update({ forceNoSelection: e.target.value === 'true' })}
          >
            <option value="false">No — player picks matching cards</option>
            <option value="true">Yes — reorder only, no selection</option>
          </select>
        </Field>
        <Field label="Reveal while searching">
          <select
            value={String(d.revealWhileSearching === true)}
            onChange={(e) => update({ revealWhileSearching: e.target.value === 'true' })}
          >
            <option value="false">No — look at privately</option>
            <option value="true">Yes — reveal to opponent</option>
          </select>
        </Field>
        <Field label="Reorder rest">
          <select
            value={String(d.reorder !== false)}
            onChange={(e) => {
              const reorder = e.target.value === 'true';
              const patch = { reorder };
              if (!reorder && d.placeRest === 'Choose') {
                patch.placeRest = 'DeckBottom';
              }
              update(patch);
            }}
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </Field>
        <Field label="Place rest">
          <select
            value={d.placeRest || 'DeckBottom'}
            onChange={(e) => update({ placeRest: e.target.value })}
          >
            {getSearchDeckPlaceRestOptions(d.reorder).map((place) => (
              <option key={place} value={place}>{place}</option>
            ))}
            {d.placeRest && !getSearchDeckPlaceRestOptions(d.reorder).includes(d.placeRest) && (
              <option value={d.placeRest}>{d.placeRest}</option>
            )}
          </select>
        </Field>
      </div>
      <h3>Deck card filters</h3>
      <p className="effects-editor__hint">
        Optional. When peeking, filters which revealed cards can be selected.
        When searching the full deck, filters which cards are found.
      </p>
      <FilterEditor
        filters={d.filters || []}
        onChange={(filters) => update({ filters })}
        steps={steps}
        traitSuggestions={traitSuggestions}
        nameSuggestions={nameSuggestions}
      />
    </>
  );
}

function ZoneTagsInput({ zones, onChange, options = ZONES }) {
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const selected = parseZonesFromEditor(zones);

  const suggestions = options.filter(
    (zone) => !selected.includes(zone)
      && (!query.trim() || zone.toLowerCase().includes(query.trim().toLowerCase())),
  );

  const addZone = (zone) => {
    const trimmed = String(zone ?? '').trim();
    if (!trimmed || selected.includes(trimmed)) return;
    onChange([...selected, trimmed]);
    setQuery('');
    setShowSuggestions(false);
  };

  const removeZone = (zone) => {
    onChange(selected.filter((z) => z !== zone));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const exact = options.find(
        (zone) => zone.toLowerCase() === query.trim().toLowerCase() && !selected.includes(zone),
      );
      if (exact) {
        addZone(exact);
      } else if (suggestions[0]) {
        addZone(suggestions[0]);
      }
    } else if (e.key === 'Backspace' && !query && selected.length > 0) {
      onChange(selected.slice(0, -1));
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div className="effects-editor__zone-tags-field">
      <div className="effects-editor__zone-tags-input">
        {selected.map((zone) => (
          <span key={zone} className="effects-editor__zone-tag">
            {zone}
            <button
              type="button"
              className="effects-editor__zone-tag-remove"
              aria-label={`Remove ${zone}`}
              onClick={() => removeZone(zone)}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          className="effects-editor__zone-tags-text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => {
            window.setTimeout(() => setShowSuggestions(false), 120);
          }}
          onKeyDown={handleKeyDown}
          placeholder={selected.length === 0 ? 'Type a zone, Enter to add…' : 'Add zone…'}
        />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <ul className="effects-editor__zone-tags-suggestions">
          {suggestions.map((zone) => (
            <li key={zone}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  addZone(zone);
                }}
              >
                {zone}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PassiveMultipliersEditor({
  multipliers = [],
  onChange,
  traitSuggestions = [],
  nameSuggestions = [],
}) {
  return (
    <section>
      <h3>Multipliers</h3>
      <p className="effects-editor__hint">
        Optional. Scales the stat modification (e.g. forEvery: floor(matching cards ÷ amount)).
      </p>
      <div className="effects-editor__actions">
        <button
          type="button"
          className="effects-editor__btn"
          onClick={() => onChange([...multipliers, createPassiveMultiplier()])}
        >
          Add multiplier
        </button>
      </div>
      {multipliers.map((multiplier, index) => (
        <div className="effects-editor__card" key={`multiplier-${index}`}>
          <div className="effects-editor__row">
            <Field label="Type">
              <select
                value={multiplier.type}
                onChange={(e) => {
                  if (e.target.value === 'forEvery') {
                    onChange(updateListItem(multipliers, index, createPassiveMultiplier('forEvery')));
                  }
                }}
              >
                {PASSIVE_MULTIPLIER_TYPES.map((type) => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
                {!PASSIVE_MULTIPLIER_TYPES.some((t) => t.id === multiplier.type) && (
                  <option value={multiplier.type}>{multiplier.type}</option>
                )}
              </select>
            </Field>
            {multiplier.type === 'forEvery' && (
              <>
                <Field label="Scope">
                  <select
                    value={multiplier.scope || 'Player'}
                    onChange={(e) =>
                      onChange(updateListItem(multipliers, index, { scope: e.target.value }))
                    }
                  >
                    {TARGET_SCOPES.map((scope) => (
                      <option key={scope} value={scope}>{scope}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Zones">
                  <ZoneTagsInput
                    zones={multiplier.zone}
                    onChange={(zone) =>
                      onChange(updateListItem(multipliers, index, { zone }))
                    }
                  />
                </Field>
                <Field label="Per (amount)">
                  <input
                    type="number"
                    min="1"
                    value={multiplier.amount ?? 1}
                    onChange={(e) =>
                      onChange(updateListItem(multipliers, index, { amount: e.target.value }))
                    }
                  />
                </Field>
              </>
            )}
          </div>
          {multiplier.type === 'forEvery' && (
            <>
              <h3>Multiplier filters</h3>
              <p className="effects-editor__hint">Optional. Which cards in the zone are counted.</p>
              <FilterEditor
                filters={multiplier.filters || []}
                onChange={(filters) =>
                  onChange(updateListItem(multipliers, index, { filters }))
                }
                traitSuggestions={traitSuggestions}
                nameSuggestions={nameSuggestions}
              />
            </>
          )}
          <button
            type="button"
            className="effects-editor__btn effects-editor__btn--danger"
            onClick={() => onChange(removeListItem(multipliers, index))}
          >
            Remove multiplier
          </button>
        </div>
      ))}
    </section>
  );
}

function ReturnDonsDetailsEditor({ details, optional, onChangeDetails, onChangeOptional }) {
  const d = details || createDefaultReturnDonsDetails();
  const update = (patch) => onChangeDetails({ ...d, ...patch });

  return (
    <div className="effects-editor__row">
      <Field label="Amount">
        <input
          type="number"
          min="1"
          value={d.amount ?? 1}
          onChange={(e) => update({ amount: e.target.value })}
        />
      </Field>
      <Field label="Optional">
        <select
          value={String(optional)}
          onChange={(e) => onChangeOptional(e.target.value === 'true')}
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </Field>
      <Field label="Prompt">
        <input
          value={d.prompt || ''}
          onChange={(e) => update({ prompt: e.target.value })}
          placeholder="Choose {amount} DON!! to return..."
        />
      </Field>
    </div>
  );
}

function RevealDetailsEditor({ details, onChange, steps = [] }) {
  const d = details || createDefaultRevealDetails();
  const update = (patch) => onChange({ ...d, ...patch });

  return (
    <div className="effects-editor__row">
      <Field label="Who reveals">
        <select
          value={d.whoReveals || 'Opponent'}
          onChange={(e) => update({ whoReveals: e.target.value })}
        >
          {REVEAL_WHO_OPTIONS.map((who) => (
            <option key={who} value={who}>{who}</option>
          ))}
          {d.whoReveals && !REVEAL_WHO_OPTIONS.includes(d.whoReveals) && (
            <option value={d.whoReveals}>{d.whoReveals}</option>
          )}
        </select>
      </Field>
      <Field label="Card step ref">
        <StepRefSelect
          value={d.cardRefId || ''}
          onChange={(cardRefId) => update({ cardRefId })}
          steps={steps}
          stepTypes={REVEAL_CARD_REF_STEP_TYPES}
          placeholder="Select prior target or search step…"
        />
      </Field>
    </div>
  );
}

function getStepSummary(step) {
  const id = step.stepId ? ` · ${step.stepId}` : '';
  if (step.type === 'action') return `${step.actionType || 'action'}${id}`;
  if (step.type === 'reveal') {
    const who = step.revealDetails?.whoReveals || 'Opponent';
    const ref = step.revealDetails?.cardRefId || '(no ref)';
    return `reveal to ${who} · ${ref}${id}`;
  }
  return `${step.type}${id}`;
}

function ConfirmModal({
  open,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isLoading = false,
  onConfirm,
  onClose,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !isLoading) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, isLoading]);

  if (!open) return null;

  return (
    <div className="effects-editor__modal-backdrop">
      {!isLoading && (
        <button
          type="button"
          className="effects-editor__modal-backdrop-dismiss"
          onClick={onClose}
          aria-label="Close dialog"
        />
      )}
      <div
        className="effects-editor__modal effects-editor__modal--confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="effects-editor-confirm-title"
      >
        <div className="effects-editor__modal-header">
          <h2 id="effects-editor-confirm-title">{title}</h2>
          <button
            type="button"
            className="effects-editor__modal-close"
            onClick={onClose}
            disabled={isLoading}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="effects-editor__modal-body">{children}</div>
        <div className="effects-editor__modal-actions">
          <button
            type="button"
            className="effects-editor__btn"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="effects-editor__btn effects-editor__btn--danger"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Deleting…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function StepEditor({ steps, onChange, traitSuggestions = [], nameSuggestions = [] }) {
  const [collapsedStepIds, setCollapsedStepIds] = useState(() => new Set());

  const toggleStepCollapsed = (stepId) => {
    setCollapsedStepIds((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
  };

  const removeStep = (index) => {
    const stepId = steps[index]?.stepId;
    onChange(removeListItem(steps, index));
    if (stepId) {
      setCollapsedStepIds((prev) => {
        const next = new Set(prev);
        next.delete(stepId);
        return next;
      });
    }
  };

  const addStep = (type) => {
    const creators = {
      target: () => createTargetStep(steps.length),
      action: () => createActionStep(steps.length),
      if: createIfStep,
      searchDeck: createSearchDeckStep,
      returnDons: () => createReturnDonsStep(steps.length),
      prompt: () => createPromptStep(steps.length),
      reveal: () => createRevealStep(steps.length),
    };
    onChange([...steps, creators[type]()]);
  };

  return (
    <section>
      <div className="effects-editor__section-header">
        <h3>Effect steps (v2)</h3>
        {steps.length > 0 && (
          <div className="effects-editor__card-header-actions">
            <button
              type="button"
              className="effects-editor__btn"
              onClick={() => setCollapsedStepIds(
                new Set(steps.map((s, i) => s.stepId || `__step_${i}`)),
              )}
            >
              Collapse all
            </button>
            <button
              type="button"
              className="effects-editor__btn"
              onClick={() => setCollapsedStepIds(new Set())}
            >
              Expand all
            </button>
          </div>
        )}
      </div>
      <p className="effects-editor__hint">
        Build multi-step effects: target selection, actions, conditionals, deck search, DON!! return, prompts, and reveal.
      </p>

      {steps.map((step, index) => {
        const collapseKey = step.stepId || `__step_${index}`;
        const isCollapsed = collapsedStepIds.has(collapseKey);

        return (
        <div
          className={`effects-editor__card${isCollapsed ? ' effects-editor__card--collapsed' : ''}`}
          key={`step-${index}`}
        >
          <div className="effects-editor__card-header">
            <div className="effects-editor__card-header-title">
              <button
                type="button"
                className="effects-editor__btn effects-editor__btn--icon effects-editor__card-toggle"
                aria-expanded={!isCollapsed}
                aria-label={isCollapsed ? 'Expand step' : 'Collapse step'}
                title={isCollapsed ? 'Expand step' : 'Collapse step'}
                onClick={() => toggleStepCollapsed(collapseKey)}
              >
                {isCollapsed ? '▶' : '▼'}
              </button>
              <strong>
                Step {index + 1}: {step.type}
                {isCollapsed && (
                  <span className="effects-editor__card-summary"> — {getStepSummary(step)}</span>
                )}
              </strong>
            </div>
            <div className="effects-editor__card-header-actions">
              <button
                type="button"
                className="effects-editor__btn effects-editor__btn--icon"
                disabled={index === 0}
                aria-label="Move step up"
                title="Move step up"
                onClick={() => onChange(moveListItem(steps, index, -1))}
              >
                ↑
              </button>
              <button
                type="button"
                className="effects-editor__btn effects-editor__btn--icon"
                disabled={index === steps.length - 1}
                aria-label="Move step down"
                title="Move step down"
                onClick={() => onChange(moveListItem(steps, index, 1))}
              >
                ↓
              </button>
              <button
                type="button"
                className="effects-editor__btn effects-editor__btn--danger"
                onClick={() => removeStep(index)}
              >
                Remove step
              </button>
            </div>
          </div>

          {!isCollapsed && (
          <>
          <div className="effects-editor__row">
            <Field label="Step ID">
              <input
                value={step.stepId}
                onChange={(e) =>
                  onChange(updateListItem(steps, index, { stepId: e.target.value }))
                }
              />
            </Field>
            <Field label="Step type">
              <select
                value={step.type}
                onChange={(e) => {
                  const type = e.target.value;
                  const replacements = {
                    target: createTargetStep(index),
                    action: createActionStep(index),
                    if: createIfStep(),
                    searchDeck: createSearchDeckStep(),
                    returnDons: createReturnDonsStep(index),
                    prompt: createPromptStep(index),
                    reveal: createRevealStep(index),
                  };
                  onChange(updateListItem(steps, index, replacements[type]));
                }}
              >
                {STEP_TYPES.map((stepType) => (
                  <option key={stepType.id} value={stepType.id}>{stepType.label}</option>
                ))}
              </select>
            </Field>
          </div>

          {step.type === 'target' && (
            <>
              <div className="effects-editor__row">
                <Field label="Targeting player">
                  <select
                    value={step.targetingPlayer || 'Player'}
                    onChange={(e) =>
                      onChange(updateListItem(steps, index, { targetingPlayer: e.target.value }))
                    }
                  >
                    {SCOPES.map((scope) => (
                      <option key={scope} value={scope}>{scope}</option>
                    ))}
                    {step.targetingPlayer && !SCOPES.includes(step.targetingPlayer) && (
                      <option value={step.targetingPlayer}>{step.targetingPlayer}</option>
                    )}
                  </select>
                </Field>
                <Field label="Scope">
                  <select
                    value={step.targetCriteria.scope}
                    onChange={(e) =>
                      onChange(updateListItem(steps, index, {
                        targetCriteria: { ...step.targetCriteria, scope: e.target.value },
                      }))
                    }
                  >
                    {TARGET_SCOPES.map((scope) => (
                      <option key={scope} value={scope}>{scope}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Zone (comma for multiple)">
                  <input
                    value={step.targetCriteria.zone}
                    onChange={(e) =>
                      onChange(updateListItem(steps, index, {
                        targetCriteria: { ...step.targetCriteria, zone: e.target.value },
                      }))
                    }
                    list={`zones-${index}`}
                  />
                  <datalist id={`zones-${index}`}>
                    {ZONES.map((zone) => <option key={zone} value={zone} />)}
                  </datalist>
                </Field>
                <Field label="Max targets">
                  <input
                    type="number"
                    min="1"
                    value={step.targetCriteria.maxTargets}
                    onChange={(e) =>
                      onChange(updateListItem(steps, index, {
                        targetCriteria: { ...step.targetCriteria, maxTargets: e.target.value },
                      }))
                    }
                  />
                </Field>
                <Field label="Optional">
                  <select
                    value={String(step.targetCriteria.optional)}
                    onChange={(e) =>
                      onChange(updateListItem(steps, index, {
                        targetCriteria: {
                          ...step.targetCriteria,
                          optional: e.target.value === 'true',
                        },
                      }))
                    }
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </Field>
                <Field label="Prompt">
                  <input
                    value={step.prompt || ''}
                    onChange={(e) =>
                      onChange(updateListItem(steps, index, { prompt: e.target.value }))
                    }
                  />
                </Field>
              </div>
              <h3>Target filters</h3>
              <FilterEditor
                filters={step.targetCriteria.filters}
                onChange={(filters) =>
                  onChange(updateListItem(steps, index, {
                    targetCriteria: { ...step.targetCriteria, filters },
                  }))
                }
                steps={steps}
                traitSuggestions={traitSuggestions}
                nameSuggestions={nameSuggestions}
              />
            </>
          )}

          {step.type === 'action' && (
            <>
              <div className="effects-editor__row">
                <Field label="Action type">
                  <select
                    value={step.actionType}
                    onChange={(e) => {
                      const actionType = e.target.value;
                      const template = ACTION_DETAIL_TEMPLATES[actionType] || {};
                      onChange(updateListItem(steps, index, {
                        actionType,
                        detailsJson: JSON.stringify(template, null, 2),
                      }));
                    }}
                  >
                    {ACTION_TYPES.map((actionType) => (
                      <option key={actionType} value={actionType}>{actionType}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <ActionDetailsEditor
                actionType={step.actionType}
                detailsJson={step.detailsJson}
                steps={steps}
                traitSuggestions={traitSuggestions}
                nameSuggestions={nameSuggestions}
                onChange={(detailsJson) =>
                  onChange(updateListItem(steps, index, { detailsJson }))
                }
              />
            </>
          )}

          {step.type === 'if' && (
            <>
              <div className="effects-editor__actions">
                <button
                  type="button"
                  className="effects-editor__btn"
                  onClick={() =>
                    onChange(updateListItem(steps, index, {
                      checks: [...step.checks, createIfCheck()],
                    }))
                  }
                >
                  Add check
                </button>
              </div>
              {step.checks.map((check, checkIndex) => {
                const checkDef = getIfStepCheckDef(check.check);
                return (
                  <div className="effects-editor__card" key={`check-${checkIndex}`}>
                  <div className="effects-editor__row">
                    <Field label="Check">
                      <select
                        value={check.check}
                        onChange={(e) => {
                          const nextCheck = e.target.value;
                          const nextDef = getIfStepCheckDef(nextCheck);
                          const next = getIfCheckDefaults(nextCheck, { traitSuggestions });
                          if (nextDef.fields.includes('refId')) {
                            next.refId = check.refId || next.refId;
                          }
                          if (!nextDef.fields.includes('filters')) {
                            delete next.filters;
                          }
                          onChange(updateListItem(steps, index, {
                            checks: updateListItem(step.checks, checkIndex, next),
                          }));
                        }}
                      >
                        {IF_STEP_CHECK_TYPES.map((checkType) => (
                          <option key={checkType.id} value={checkType.id}>{checkType.label}</option>
                        ))}
                        {!IF_STEP_CHECK_TYPES.some((t) => t.id === check.check) && (
                          <option value={check.check}>{check.check}</option>
                        )}
                      </select>
                    </Field>
                    {checkDef.fields.includes('refId') && (
                      <Field label="Prior step">
                        <StepRefSelect
                          value={check.refId || ''}
                          onChange={(refId) =>
                            onChange(updateListItem(steps, index, {
                              checks: updateListItem(step.checks, checkIndex, { refId }),
                            }))
                          }
                          steps={steps}
                          placeholder="Select target or search step…"
                        />
                      </Field>
                    )}
                    {checkDef.fields.includes('comparison') && checkDef.comparisons && (
                      <Field label="Comparison">
                        <select
                          value={check.comparison || checkDef.defaultComparison || ''}
                          onChange={(e) =>
                            onChange(updateListItem(steps, index, {
                              checks: updateListItem(step.checks, checkIndex, {
                                comparison: e.target.value,
                              }),
                            }))
                          }
                        >
                          {checkDef.comparisons.map((comparison) => (
                            <option key={comparison} value={comparison}>{comparison}</option>
                          ))}
                        </select>
                      </Field>
                    )}
                    {checkDef.fields.includes('value') && (
                      <Field label={checkDef.valueLabel || 'Value'}>
                        <input
                          type={checkDef.numericValue ? 'number' : 'text'}
                          value={check.value ?? ''}
                          onChange={(e) =>
                            onChange(updateListItem(steps, index, {
                              checks: updateListItem(step.checks, checkIndex, { value: e.target.value }),
                            }))
                          }
                          list={
                            check.check === 'leaderTraits' && traitSuggestions.length > 0
                              ? `if-leader-trait-suggestions-${index}-${checkIndex}`
                              : undefined
                          }
                        />
                        {check.check === 'leaderTraits' && traitSuggestions.length > 0 && (
                          <datalist id={`if-leader-trait-suggestions-${index}-${checkIndex}`}>
                            {traitSuggestions.map((trait) => (
                              <option key={trait} value={trait} />
                            ))}
                          </datalist>
                        )}
                      </Field>
                    )}
                    <div style={{ alignSelf: 'end' }}>
                      <button
                        type="button"
                        className="effects-editor__btn effects-editor__btn--danger"
                        onClick={() =>
                          onChange(updateListItem(steps, index, {
                            checks: removeListItem(step.checks, checkIndex),
                          }))
                        }
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  {checkDef.fields.includes('filters') && (
                    <>
                      <h3>{checkDef.filtersLabel || 'Filters'}</h3>
                      <p className="effects-editor__hint">
                        {checkDef.filtersHint || 'Optional.'}
                      </p>
                      <FilterEditor
                        filters={check.filters || []}
                        onChange={(filters) =>
                          onChange(updateListItem(steps, index, {
                            checks: updateListItem(step.checks, checkIndex, { filters }),
                          }))
                        }
                        steps={steps}
                        traitSuggestions={traitSuggestions}
                        nameSuggestions={nameSuggestions}
                      />
                    </>
                  )}
                  </div>
                );
              })}
            </>
          )}

          {step.type === 'prompt' && (
            <Field label="Prompt">
              <input
                value={step.prompt || ''}
                onChange={(e) =>
                  onChange(updateListItem(steps, index, { prompt: e.target.value }))
                }
                placeholder="Take 1 life card to your hand?"
              />
            </Field>
          )}

          {step.type === 'returnDons' && (
            <ReturnDonsDetailsEditor
              details={step.returnDonsDetails}
              optional={step.optional ?? false}
              onChangeDetails={(returnDonsDetails) =>
                onChange(updateListItem(steps, index, { returnDonsDetails }))
              }
              onChangeOptional={(optional) =>
                onChange(updateListItem(steps, index, { optional }))
              }
            />
          )}

          {step.type === 'reveal' && (
            <RevealDetailsEditor
              details={step.revealDetails}
              steps={steps}
              onChange={(revealDetails) =>
                onChange(updateListItem(steps, index, { revealDetails }))
              }
            />
          )}

          {step.type === 'searchDeck' && (
            <>
              <div className="effects-editor__row">
                <Field label="Title">
                  <input
                    value={step.title || ''}
                    onChange={(e) =>
                      onChange(updateListItem(steps, index, { title: e.target.value }))
                    }
                  />
                </Field>
                <Field label="Subtitle">
                  <input
                    value={step.subtitle ?? ''}
                    onChange={(e) =>
                      onChange(updateListItem(steps, index, { subtitle: e.target.value }))
                    }
                  />
                </Field>
              </div>
              <SearchDeckDetailsEditor
                details={step.searchDeckDetails}
                steps={steps}
                onChange={(searchDeckDetails) => {
                  const patch = { searchDeckDetails };
                  const wasForced = step.searchDeckDetails?.forceNoSelection === true;
                  const isForced = searchDeckDetails.forceNoSelection === true;
                  if (isForced && !wasForced) {
                    patch.title = SEARCH_DECK_FORCE_NO_SELECTION_TITLES.title;
                    patch.subtitle = SEARCH_DECK_FORCE_NO_SELECTION_TITLES.subtitle;
                  } else if (!isForced && wasForced) {
                    patch.title = SEARCH_DECK_DEFAULT_TITLES.title;
                    patch.subtitle = SEARCH_DECK_DEFAULT_TITLES.subtitle;
                  }
                  onChange(updateListItem(steps, index, patch));
                }}
                traitSuggestions={traitSuggestions}
                nameSuggestions={nameSuggestions}
              />
            </>
          )}
          </>
          )}
        </div>
        );
      })}

      <div className="effects-editor__actions effects-editor__actions--steps">
        {STEP_TYPES.map((stepType) => (
          <button
            key={stepType.id}
            type="button"
            className="effects-editor__btn"
            onClick={() => addStep(stepType.id)}
          >
            + {stepType.label}
          </button>
        ))}
      </div>
    </section>
  );
}

function CannotBeKodDetailsEditor({
  cannotBeKodDetails,
  onChange,
  steps = [],
  traitSuggestions = [],
  nameSuggestions = [],
}) {
  return (
    <>
      <div className="effects-editor__row">
        <Field label="Type">
          <select
            value={cannotBeKodDetails?.type || ''}
            onChange={(e) => {
              const type = e.target.value;
              onChange({
                ...(cannotBeKodDetails || createCannotBeKodDetails()),
                type,
                attackerFilters: type === 'ByAttack'
                  ? (cannotBeKodDetails?.attackerFilters || [])
                  : [],
              });
            }}
          >
            {CANNOT_BE_KOD_TYPES.map(({ id, label }) => (
              <option key={id || 'none'} value={id}>{label}</option>
            ))}
          </select>
        </Field>
      </div>
      {cannotBeKodDetails?.type === 'ByAttack' && (
        <>
          <h3>Attacker filters</h3>
          <p className="effects-editor__hint">
            This Character cannot be K.O.&apos;d by attackers matching these filters.
          </p>
          <FilterEditor
            filters={cannotBeKodDetails?.attackerFilters || []}
            onChange={(attackerFilters) =>
              onChange({
                ...(cannotBeKodDetails || createCannotBeKodDetails('ByAttack')),
                attackerFilters,
              })
            }
            steps={steps}
            traitSuggestions={traitSuggestions}
            nameSuggestions={nameSuggestions}
          />
        </>
      )}
    </>
  );
}

function ExtraNameDetailsEditor({ extraNameDetails, onChange, nameSuggestions = [] }) {
  return (
    <>
      <div className="effects-editor__row">
        <Field label="Extra name">
          <input
            type="text"
            value={extraNameDetails?.name || ''}
            onChange={(e) =>
              onChange({
                ...(extraNameDetails || createExtraNameDetails()),
                name: e.target.value,
              })
            }
            list={nameSuggestions.length > 0 ? 'extra-name-suggestions' : undefined}
          />
          {nameSuggestions.length > 0 && (
            <datalist id="extra-name-suggestions">
              {nameSuggestions.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          )}
        </Field>
      </div>
      <p className="effects-editor__hint">
        Alternate card name used for name-matching filters (e.g. Character [Name] references).
      </p>
    </>
  );
}

function PassiveGrantedEffectEditor({ effect, onChange, traitSuggestions = [], nameSuggestions = [] }) {
  const granted = effect || { trigger: 'Rush' };
  const nestedEffectEditor = parseAddEffectNestedEffect(granted);
  const nestedEffectJson = JSON.stringify(granted, null, 2);

  const updateTrigger = (trigger) => {
    const updated = {
      ...nestedEffectEditor,
      trigger,
      format: 'keyword',
    };
    if (trigger === 'CannotBeKod' && !updated.cannotBeKodDetails) {
      updated.cannotBeKodDetails = createCannotBeKodDetails();
    }
    if (trigger === 'ExtraName' && !updated.extraNameDetails) {
      updated.extraNameDetails = createExtraNameDetails();
    }
    onChange(buildAddEffectNestedEffect(updated));
  };

  const updateJson = (json) => {
    try {
      const parsed = JSON.parse(json || '{}');
      if (!parsed.trigger) return;
      onChange(parsed);
    } catch {
      // keep editing until valid JSON
    }
  };

  return (
    <section>
      <h3>Granted effect</h3>
      <p className="effects-editor__hint">
        Keyword or trait granted to matching cards while this passive is active.
      </p>
      {isKeywordNestedEffect(granted) ? (
        <>
          <div className="effects-editor__row">
            <Field label="Trigger">
              <select
                value={nestedEffectEditor?.trigger || 'Rush'}
                onChange={(e) => updateTrigger(e.target.value)}
              >
                {ADD_EFFECT_KEYWORD_TRIGGERS.map((trigger) => (
                  <option key={trigger} value={trigger}>{trigger}</option>
                ))}
                {nestedEffectEditor?.trigger
                  && !ADD_EFFECT_KEYWORD_TRIGGERS.includes(nestedEffectEditor.trigger) && (
                  <option value={nestedEffectEditor.trigger}>{nestedEffectEditor.trigger}</option>
                )}
              </select>
            </Field>
          </div>
          {nestedEffectEditor?.trigger === 'CannotBeKod' && (
            <CannotBeKodDetailsEditor
              cannotBeKodDetails={nestedEffectEditor.cannotBeKodDetails}
              onChange={(cannotBeKodDetails) =>
                onChange(buildAddEffectNestedEffect({
                  ...nestedEffectEditor,
                  cannotBeKodDetails,
                }))
              }
              traitSuggestions={traitSuggestions}
              nameSuggestions={nameSuggestions}
            />
          )}
          {nestedEffectEditor?.trigger === 'ExtraName' && (
            <ExtraNameDetailsEditor
              extraNameDetails={nestedEffectEditor.extraNameDetails}
              onChange={(extraNameDetails) =>
                onChange(buildAddEffectNestedEffect({
                  ...nestedEffectEditor,
                  extraNameDetails,
                }))
              }
              nameSuggestions={nameSuggestions}
            />
          )}
        </>
      ) : (
        <>
          <p className="effects-editor__hint">
            This granted effect uses effect steps; edit as JSON below.
          </p>
          <Field label="Granted effect (JSON)">
            <textarea
              value={nestedEffectJson}
              onChange={(e) => updateJson(e.target.value)}
              rows={Math.min(16, Math.max(6, nestedEffectJson.split('\n').length + 1))}
            />
          </Field>
        </>
      )}
    </section>
  );
}

function EffectForm({ effect, onChange, traitSuggestions = [], nameSuggestions = [] }) {
  return (
    <div>
      <div className="effects-editor__row">
        <Field label="Trigger">
          <select
            value={effect.trigger}
            onChange={(e) => {
              const trigger = e.target.value;
              const patch = { trigger };
              if (trigger === 'CannotBeKod') {
                patch.format = 'keyword';
                if (!effect.cannotBeKodDetails) {
                  patch.cannotBeKodDetails = createCannotBeKodDetails();
                }
              }
              if (trigger === 'ExtraName') {
                patch.format = 'keyword';
                if (!effect.extraNameDetails) {
                  patch.extraNameDetails = createExtraNameDetails();
                }
              }
              onChange(patch);
            }}
          >
            {TRIGGERS.map((trigger) => (
              <option key={trigger} value={trigger}>{trigger}</option>
            ))}
          </select>
        </Field>
        <Field label="Restriction">
          <select
            value={effect.restriction}
            onChange={(e) => onChange({ restriction: e.target.value })}
          >
            {RESTRICTIONS.map((restriction) => (
              <option key={restriction || 'none'} value={restriction}>
                {restriction || '(none)'}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Effect format">
          <select
            value={effect.format}
            onChange={(e) => {
              const format = e.target.value;
              const patch = { format };
              if (format === 'steps' && (!effect.steps || effect.steps.length === 0)) {
                patch.steps = [createTargetStep(), createActionStep()];
              }
              onChange(patch);
            }}
          >
            {EFFECT_FORMATS.map((format) => (
              <option key={format.id} value={format.id}>{format.label}</option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Description">
        <textarea
          value={effect.description}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={3}
          style={{ minHeight: '72px' }}
        />
      </Field>

      <PreConditionEditor
        preConditions={effect.preConditions}
        onChange={(preConditions) => onChange({ preConditions })}
        steps={effect.steps || []}
        traitSuggestions={traitSuggestions}
        nameSuggestions={nameSuggestions}
      />

      {effect.trigger === 'CannotBeKod' && (
        <section>
          <h3>CannotBeKod details</h3>
          <CannotBeKodDetailsEditor
            cannotBeKodDetails={effect.cannotBeKodDetails}
            onChange={(cannotBeKodDetails) => onChange({ cannotBeKodDetails })}
            steps={effect.steps || []}
            traitSuggestions={traitSuggestions}
            nameSuggestions={nameSuggestions}
          />
        </section>
      )}

      {effect.trigger === 'ExtraName' && (
        <section>
          <h3>ExtraName details</h3>
          <ExtraNameDetailsEditor
            extraNameDetails={effect.extraNameDetails}
            onChange={(extraNameDetails) => onChange({ extraNameDetails })}
            nameSuggestions={nameSuggestions}
          />
        </section>
      )}

      {effect.format === 'passive' && (
        <section>
          <h3>Passive effect details</h3>
          <div className="effects-editor__row">
            <Field label="Type">
              <select
                value={effect.effectDetails.type || 'StatModifier'}
                onChange={(e) => {
                  const { scope, filters } = effect.effectDetails;
                  onChange({
                    effectDetails: {
                      ...createPassiveEffectDetails(e.target.value),
                      scope,
                      filters,
                    },
                  });
                }}
              >
                {PASSIVE_EFFECT_TYPES.map(({ id, label }) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
            </Field>
            <Field label="Scope">
              <select
                value={effect.effectDetails.scope}
                onChange={(e) =>
                  onChange({
                    effectDetails: { ...effect.effectDetails, scope: e.target.value },
                  })
                }
              >
                {PASSIVE_SCOPES.map((scope) => (
                  <option key={scope} value={scope}>{scope}</option>
                ))}
                {effect.effectDetails.scope
                  && !PASSIVE_SCOPES.includes(effect.effectDetails.scope) && (
                  <option value={effect.effectDetails.scope}>{effect.effectDetails.scope}</option>
                )}
              </select>
            </Field>
            {(effect.effectDetails.type || 'StatModifier') === 'StatModifier' && (
              <>
                <Field label="Stat">
                  <select
                    value={effect.effectDetails.modifications[0]?.stat || 'power'}
                    onChange={(e) =>
                      onChange({
                        effectDetails: {
                          ...effect.effectDetails,
                          modifications: [{
                            ...effect.effectDetails.modifications[0],
                            stat: e.target.value,
                          }],
                        },
                      })
                    }
                  >
                    {PASSIVE_MODIFIER_STATS.map((stat) => (
                      <option key={stat} value={stat}>{stat}</option>
                    ))}
                    {effect.effectDetails.modifications[0]?.stat
                      && !PASSIVE_MODIFIER_STATS.includes(effect.effectDetails.modifications[0].stat) && (
                      <option value={effect.effectDetails.modifications[0].stat}>
                        {effect.effectDetails.modifications[0].stat}
                      </option>
                    )}
                  </select>
                </Field>
                <Field label="Amount">
                  <input
                    type="number"
                    value={effect.effectDetails.modifications[0]?.amount ?? 0}
                    onChange={(e) =>
                      onChange({
                        effectDetails: {
                          ...effect.effectDetails,
                          modifications: [{
                            ...effect.effectDetails.modifications[0],
                            amount: e.target.value,
                          }],
                        },
                      })
                    }
                  />
                </Field>
              </>
            )}
          </div>
          <h3>Target filters</h3>
          <FilterEditor
            filters={effect.effectDetails.filters}
            onChange={(filters) =>
              onChange({ effectDetails: { ...effect.effectDetails, filters } })
            }
            steps={effect.steps || []}
            traitSuggestions={traitSuggestions}
            nameSuggestions={nameSuggestions}
          />
          {(effect.effectDetails.type || 'StatModifier') === 'StatModifier' && (
            <PassiveMultipliersEditor
              multipliers={effect.effectDetails.multipliers || []}
              onChange={(multipliers) =>
                onChange({ effectDetails: { ...effect.effectDetails, multipliers } })
              }
              traitSuggestions={traitSuggestions}
              nameSuggestions={nameSuggestions}
            />
          )}
          {effect.effectDetails.type === 'AddEffect' && (
            <PassiveGrantedEffectEditor
              effect={effect.effectDetails.effect}
              onChange={(grantedEffect) =>
                onChange({ effectDetails: { ...effect.effectDetails, effect: grantedEffect } })
              }
              traitSuggestions={traitSuggestions}
              nameSuggestions={nameSuggestions}
            />
          )}
        </section>
      )}

      {effect.format === 'steps' && (
        <StepEditor
          steps={effect.steps}
          onChange={(steps) => onChange({ steps })}
          traitSuggestions={traitSuggestions}
          nameSuggestions={nameSuggestions}
        />
      )}
    </div>
  );
}

export default function EffectsEditorPage() {
  const [selectedCard, setSelectedCard] = useState(null);
  const [listSearch, setListSearch] = useState('');
  const [listStatusFilter, setListStatusFilter] = useState('all');
  const [listSourceFilter, setListSourceFilter] = useState('all');

  if (!selectedCard) {
    return (
      <EffectsEditorCardList
        onSelectCard={setSelectedCard}
        search={listSearch}
        onSearchChange={setListSearch}
        statusFilter={listStatusFilter}
        onStatusFilterChange={setListStatusFilter}
        sourceFilter={listSourceFilter}
        onSourceFilterChange={setListSourceFilter}
      />
    );
  }

  return (
    <EffectsEditorWorkspace
      selectedCard={selectedCard}
      onBack={() => setSelectedCard(null)}
    />
  );
}

function EffectsEditorWorkspace({ selectedCard, onBack }) {
  const [cards, setCards] = useState([]);
  const [effectSource, setEffectSource] = useState(null);
  const [activeEffectIndex, setActiveEffectIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  const loadCardEffects = useCallback(async () => {
    setIsLoading(true);
    setActiveEffectIndex(0);
    setStatus({ type: '', message: '' });
    try {
      const { source, effect: rawEffects = [] } = await getCardEffect(selectedCard.id);
      setEffectSource(source);
      const effects = rawEffects.length > 0
        ? rawEffects.map((raw) => importEffect(raw))
        : createEffectsFromCard(selectedCard);
      setCards([{ cardId: selectedCard.id, effects }]);
    } catch (error) {
      setEffectSource(null);
      setCards([createCardEntry(selectedCard)]);
      setStatus({ type: 'error', message: error.message || 'Failed to load card effects' });
    } finally {
      setIsLoading(false);
    }
  }, [selectedCard]);

  useEffect(() => {
    loadCardEffects();
  }, [loadCardEffects]);

  const activeCard = cards[0];
  const effects = activeCard?.effects || [];

  const outputJson = useMemo(() => {
    try {
      return buildMultiCardEffectsJson(cards);
    } catch (error) {
      return `// Error building JSON: ${error.message}`;
    }
  }, [cards]);

  const traitSuggestions = useMemo(
    () => extractTraitsFromEffectText(selectedCard?.effectText),
    [selectedCard?.effectText]
  );

  const nameSuggestions = useMemo(
    () => extractCardNamesFromEffectText(selectedCard?.effectText),
    [selectedCard?.effectText]
  );

  const updateEffects = (nextEffects) => {
    setCards([{ cardId: selectedCard.id, effects: nextEffects }]);
  };

  const updateEffect = (index, patch) => {
    updateEffects(updateListItem(effects, index, patch));
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(outputJson);
      setStatus({ type: 'success', message: 'JSON copied to clipboard.' });
    } catch {
      setStatus({ type: 'error', message: 'Could not copy to clipboard.' });
    }
  };

  const validateJson = () => {
    try {
      JSON.parse(outputJson);
      setStatus({ type: 'success', message: 'Generated JSON is valid.' });
    } catch (error) {
      setStatus({ type: 'error', message: `Invalid JSON: ${error.message}` });
    }
  };

  const saveEffects = async () => {
    try {
      setIsSaving(true);
      const effectPayload = effects.map(buildEffect);
      await saveCardEffect(selectedCard.id, effectPayload);
      setEffectSource(EFFECT_SOURCE.DB);
      setStatus({ type: 'success', message: `Saved ${selectedCard.id} to database.` });
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to save effects';
      setStatus({ type: 'error', message });
    } finally {
      setIsSaving(false);
    }
  };

  const clearDbEffect = async () => {
    try {
      setIsDeleting(true);
      await deleteCardEffect(selectedCard.id);
      setShowDeleteConfirm(false);
      await loadCardEffects();
      setStatus({
        type: 'success',
        message: `Cleared database effect for ${selectedCard.id}.`,
      });
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to delete effect';
      setStatus({ type: 'error', message });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="effects-editor">
      <ConfirmModal
        open={showDeleteConfirm}
        title="Clear database effect?"
        confirmLabel="Clear from database"
        isLoading={isDeleting}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={clearDbEffect}
      >
        <p>
          This removes all saved effect versions for{' '}
          <strong>{selectedCard.name}</strong> ({selectedCard.id}) from the database.
        </p>
        <p className="effects-editor__hint">
          If a code implementation exists for this card, it will be loaded afterward.
          Otherwise the editor will start from the card&apos;s effect text.
        </p>
      </ConfirmModal>

      <header className="effects-editor__header">
        <div className="effects-editor__header-row">
          <div>
            <button type="button" className="effects-editor__btn" onClick={onBack}>
              ← Back to card list
            </button>
            <h1>{selectedCard.name}</h1>
            <p>
              {selectedCard.id} — edit effect definitions for this card.
              {effectSource && !isLoading && (
                <span className={`effects-editor__source-badge effects-editor__source-badge--${effectSource}`}>
                  {' '}
                  Source: {effectSource === EFFECT_SOURCE.DB && 'Database'}
                  {effectSource === EFFECT_SOURCE.CODE && 'Code'}
                  {effectSource === EFFECT_SOURCE.NONE && 'None'}
                </span>
              )}
            </p>
          </div>
          <div className="effects-editor__header-actions">
            {effectSource === EFFECT_SOURCE.DB && (
              <button
                type="button"
                className="effects-editor__btn effects-editor__btn--danger"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isLoading || isSaving || isDeleting}
              >
                Clear database effect
              </button>
            )}
            <button
              type="button"
              className="effects-editor__btn effects-editor__btn--primary"
              onClick={saveEffects}
              disabled={isSaving || isLoading || isDeleting}
            >
              {isSaving ? 'Saving…' : 'Save to database'}
            </button>
          </div>
        </div>
      </header>

      {isLoading ? (
        <p className="effects-editor__hint">Loading effects…</p>
      ) : (
        <div className="effects-editor__layout">
          <div className="effects-editor__panel">
            <CardPreview card={selectedCard} />

            <div className="effects-editor__row">
              <Field label="Card">
                <input value={selectedCard.id} readOnly />
              </Field>
            </div>

            <div className="effects-editor__tabs">
              {effects.map((effect, index) => (
                <button
                  key={`effect-tab-${index}`}
                  type="button"
                  className={`effects-editor__tab${index === activeEffectIndex ? ' effects-editor__tab--active' : ''}`}
                  onClick={() => setActiveEffectIndex(index)}
                >
                  Effect {index + 1}: {effect.trigger}
                </button>
              ))}
              <button
                type="button"
                className="effects-editor__btn"
                onClick={() => {
                  updateEffects([...effects, createEffect()]);
                  setActiveEffectIndex(effects.length);
                }}
              >
                + Add effect
              </button>
            </div>

            {effects[activeEffectIndex] && (
              <>
                <div className="effects-editor__actions">
                  <button
                    type="button"
                    className="effects-editor__btn effects-editor__btn--danger"
                    onClick={() => {
                      if (effects.length === 1) {
                        updateEffects(createEffectsFromCard(selectedCard));
                        setActiveEffectIndex(0);
                        return;
                      }
                      const next = removeListItem(effects, activeEffectIndex);
                      updateEffects(next);
                      setActiveEffectIndex(Math.max(0, activeEffectIndex - 1));
                    }}
                  >
                    Remove current effect
                  </button>
                </div>
                <EffectForm
                  effect={effects[activeEffectIndex]}
                  onChange={(patch) => updateEffect(activeEffectIndex, patch)}
                  traitSuggestions={traitSuggestions}
                  nameSuggestions={nameSuggestions}
                />
              </>
            )}
          </div>

          <div className="effects-editor__panel effects-editor__output">
            <h2>Generated JSON</h2>
            <textarea readOnly value={outputJson} />
            <div className="effects-editor__actions">
              <button
                type="button"
                className="effects-editor__btn effects-editor__btn--primary"
                onClick={saveEffects}
                disabled={isSaving}
              >
                {isSaving ? 'Saving…' : 'Save to database'}
              </button>
              <button type="button" className="effects-editor__btn" onClick={copyJson}>
                Copy JSON
              </button>
              <button type="button" className="effects-editor__btn" onClick={validateJson}>
                Validate JSON
              </button>
            </div>
            {status.message && (
              <p className={`effects-editor__status${status.type === 'error' ? ' effects-editor__status--error' : ''}`}>
                {status.message}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
